import { useState, useEffect, useRef } from 'react';

interface UseScrollSpyOptions {
  sectionIds: string[];
  offset?: number;
}

export const useScrollSpy = ({ sectionIds, offset = 100 }: UseScrollSpyOptions) => {
  const [activeSection, setActiveSection] = useState<string>('');
  const observerRef = useRef<IntersectionObserver | null>(null);
  const visibleSectionsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Calculate rootMargin based on offset
    const rootMargin = `-${offset}px 0px -50% 0px`;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (entry.isIntersecting) {
            visibleSectionsRef.current.add(id);
          } else {
            visibleSectionsRef.current.delete(id);
          }
        });

        // Find the first visible section based on the order in sectionIds
        const firstVisible = sectionIds.find((id) =>
          visibleSectionsRef.current.has(id)
        );
        
        setActiveSection(firstVisible || '');
      },
      {
        rootMargin,
        threshold: 0,
      }
    );

    // Observe all sections
    sectionIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      visibleSectionsRef.current.clear();
    };
  }, [sectionIds, offset]);

  return activeSection;
};
